import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plane, Filter, Clock, ArrowRight, Leaf, Luggage, Loader2, PlugZap } from 'lucide-react';
import SearchWidget from '../components/SearchWidget';
import IndicativeResultsNotice from '../components/IndicativeResultsNotice';
import useTravelpayoutsLoader from '../hooks/useTravelpayoutsLoader';
import { airports } from '../mock/mock';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';

// Flights follows the exact same "provider architecture" contract as
// Hotels/Cars: results here come only from approved live providers. The
// backend gates KAYAK Sandbox behind `KAYAK_SANDBOX_PRODUCTION_ENABLED`;
// when nothing is active, we render the clean "no active provider" state
// rather than fabricating flights.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;
const POLL_INTERVAL_MS = 2000;

// Feature flag: temporarily pause KAYAK Sandbox results on the Flights page
// while the Travelpayouts White Label test runs on production. The backend
// KAYAK integration, adapters, credentials, and env variables are all
// preserved — flipping this env flag back to anything other than 'false'
// (or removing it) instantly re-enables KAYAK-driven results.
const KAYAK_FLIGHTS_ENABLED = process.env.REACT_APP_FLIGHTS_KAYAK_ENABLED !== 'false';

// Feature flag: while we run the Travelpayouts White Label on /flights as the
// only customer-facing flight search, Cheapora's own SearchWidget is left in
// the codebase but NOT rendered on this page. Flip this flag back to 'true'
// (or remove it) once real provider Search APIs are approved and we resume
// Cheapora's own search UI. No CSS hiding — the widget is simply not mounted.
const CUSTOM_SEARCH_ENABLED = process.env.REACT_APP_FLIGHTS_CUSTOM_SEARCH_ENABLED !== 'false';

const priceInSGD = (price, currency) => {
  if (typeof price !== 'number') return null;
  if (!currency || currency === 'SGD') return Math.round(price);
  if (currency === 'USD') return Math.round(price * 1.35);
  return Math.round(price);
};

const NoActiveProviderCard = ({ providersUnavailable }) => {
  const visible = (providersUnavailable || []).filter(p =>
    !/sandbox_disabled_in_production|kayak/i.test(`${p.reason || ''} ${p.name || ''}`)
  );
  return (
  <Card className="border-slate-200" data-testid="flights-no-provider">
    <CardContent className="p-10 md:p-14 text-center">
      <div className="w-14 h-14 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center mx-auto">
        <PlugZap className="w-7 h-7" />
      </div>
      <div className="font-display font-extrabold text-xl text-slate-900 mt-4">
        No active flight provider available yet
      </div>
      <p className="text-slate-600 text-sm mt-2 max-w-lg mx-auto">
        We're finalising integrations with our approved flight partners. Live fares will appear
        here as soon as a provider is activated. No dummy flights are shown in the meantime.
      </p>
      {visible.length > 0 && (
        <div className="mt-5 inline-flex flex-wrap gap-2 justify-center">
          {visible.map((p, i) => (
            <Badge key={i} variant="outline" className="border-slate-300 text-slate-600 text-[11px]">
              {p.name}: {p.reason}
            </Badge>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
  );
};

export default function Flights() {
  const location = useLocation();
  const navigate = useNavigate();
  const [search, setSearch] = useState({ from: 'SIN', to: 'BKK', date: null });
  const [initialSearch, setInitialSearch] = useState(null);
  const [sort, setSort] = useState('best');
  const [maxPrice, setMaxPrice] = useState([1200]);
  const [stops, setStops] = useState({ nonstop: true, one: true });
  const [airlineFilter, setAirlineFilter] = useState({});
  const [liveResults, setLiveResults] = useState([]);
  const [liveStatus, setLiveStatus] = useState('idle'); // idle | searching | complete | no_provider | error
  const [providersUnavailable, setProvidersUnavailable] = useState([]);
  const [liveCurrency, setLiveCurrency] = useState('SGD');
  const { toast } = useToast();
  const pollRef = useRef({ tries: 0, timer: null, cancelled: false });

  // Travelpayouts White Label loader (wl_id=22421) — shared with Home so
  // navigating / <-> /flights inside the SPA does NOT re-inject or remove
  // the WL runtime. See /hooks/useTravelpayoutsLoader.js for the full
  // rationale. The #tpwl-search / #tpwl-tickets containers below are
  // rendered as static JSX before this hook runs, so the WL bundle finds
  // its mount points on both initial load and route re-entry.
  useTravelpayoutsLoader();

  useEffect(() => {
    const payload = location.state && location.state.search;
    let p = payload;
    if (!p) {
      try {
        const raw = sessionStorage.getItem('waan_last_search');
        if (raw) p = JSON.parse(raw);
      } catch(_) {}
    }
    if (p && p.type === 'flights') {
      const firstLeg = Array.isArray(p.legs) && p.legs.length ? p.legs[0] : null;
      setSearch({
        from: firstLeg?.from || p.from,
        to: firstLeg?.to || p.to,
        date: firstLeg?.depart || p.depart || null,
        legs: Array.isArray(p.legs) && p.legs.length ? p.legs : [{ from: p.from, to: p.to, depart: p.depart }],
        tripType: p.tripType || 'return',
      });
      setStops(p.directOnly ? { nonstop: true, one: false } : { nonstop: true, one: true });
      setAirlineFilter({});
      setInitialSearch(p);
    }
  }, [location.key, location.state]);

  useEffect(() => {
    // Cancel any in-flight poll and reset before a fresh search.
    pollRef.current.cancelled = true;
    if (pollRef.current.timer) clearTimeout(pollRef.current.timer);
    pollRef.current = { tries: 0, timer: null, cancelled: false };
    setLiveResults([]);
    setProvidersUnavailable([]);

    // KAYAK Sandbox is temporarily paused via env flag. We keep the state
    // machine idle so nothing renders from KAYAK, leaving the page for
    // the Travelpayouts White Label to own during the live test.
    if (!KAYAK_FLIGHTS_ENABLED) {
      setLiveStatus('idle');
      return;
    }

    const legs = Array.isArray(search.legs) && search.legs.length
      ? search.legs
      : (search.from && search.to && search.date ? [{ from: search.from, to: search.to, depart: search.date }] : []);
    if (!legs.length || legs.some(l => !l.from || !l.to || !l.depart)) {
      setLiveStatus('idle');
      return;
    }

    const run = async () => {
      setLiveStatus('searching');
      try {
        const backendLegs = legs.map(l => ({
          origin: l.from,
          destination: l.to,
          date: new Date(l.depart).toISOString().slice(0, 10),
          flex: 'exact',
        }));
        const { data } = await axios.post(`${API}/kayak/flights/search`, {
          cabin: 'Economy',
          adults: 1,
          legs: backendLegs,
        });
        if (pollRef.current.cancelled) return;
        if (data.status === 'no_provider') {
          setProvidersUnavailable(data.providers?.unavailable || []);
          setLiveStatus('no_provider');
          return;
        }
        if (!data.ok || !data.searchId) {
          setLiveStatus('error');
          return;
        }
        setLiveCurrency(data.currency || 'USD');
        setLiveResults(Array.isArray(data.results) ? data.results : []);
        if (data.status === 'complete') { setLiveStatus('complete'); return; }
        const poll = async () => {
          if (pollRef.current.cancelled) return;
          pollRef.current.tries += 1;
          try {
            const resp = await axios.post(`${API}/kayak/flights/poll`, {
              searchId: data.searchId,
              cluster: data.cluster,
              userTrackId: data.userTrackId,
            });
            if (pollRef.current.cancelled) return;
            if (resp.data.status === 'no_provider') {
              setProvidersUnavailable(resp.data.providers?.unavailable || []);
              setLiveStatus('no_provider');
              return;
            }
            if (resp.data.ok) {
              setLiveResults(Array.isArray(resp.data.results) ? resp.data.results : []);
              setLiveCurrency(resp.data.currency || 'USD');
              if (resp.data.status === 'complete' || pollRef.current.tries >= MAX_POLLS) {
                setLiveStatus('complete'); return;
              }
            } else if (pollRef.current.tries >= MAX_POLLS) {
              setLiveStatus('complete'); return;
            }
            pollRef.current.timer = setTimeout(poll, POLL_INTERVAL_MS);
          } catch (_e) {
            if (pollRef.current.tries >= MAX_POLLS) setLiveStatus(liveResults.length ? 'complete' : 'error');
            else pollRef.current.timer = setTimeout(poll, POLL_INTERVAL_MS);
          }
        };
        pollRef.current.timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (_e) {
        if (!pollRef.current.cancelled) setLiveStatus('error');
      }
    };
    run();
    return () => {
      pollRef.current.cancelled = true;
      if (pollRef.current.timer) clearTimeout(pollRef.current.timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.from, search.to, search.date, JSON.stringify(search.legs || [])]);

  const rawResults = useMemo(() =>
    liveResults.map(r => ({ ...r, price: priceInSGD(r.price, liveCurrency) }))
  , [liveResults, liveCurrency]);

  const airlinesList = useMemo(() => Array.from(new Set(rawResults.map(r => r.airline).filter(Boolean))), [rawResults]);

  const filtered = useMemo(() => {
    let list = rawResults.filter(r => r.price == null || r.price <= maxPrice[0]);
    list = list.filter(r => {
      if (r.stops === 'Non-stop' && !stops.nonstop) return false;
      if (r.stops && r.stops !== 'Non-stop' && !stops.one) return false;
      return true;
    });
    const activeAirlines = Object.entries(airlineFilter).filter(([,v]) => v).map(([k]) => k);
    if (activeAirlines.length) list = list.filter(r => activeAirlines.includes(r.airline));
    if (sort === 'cheapest') list = [...list].sort((a,b) => (a.price ?? 9e9) - (b.price ?? 9e9));
    if (sort === 'fastest') list = [...list].sort((a,b) => (a.duration || '').localeCompare(b.duration || ''));
    return list;
  }, [rawResults, maxPrice, stops, airlineFilter, sort]);

  const cityName = (code) => airports.find(a => a.code === code)?.city || code;
  const routeHeading = useMemo(() => {
    const list = Array.isArray(search.legs) && search.legs.length ? search.legs : [{ from: search.from, to: search.to }];
    if (
      search.tripType === 'return' && list.length === 2 &&
      list[0].from === list[1].to && list[0].to === list[1].from
    ) {
      return `${cityName(list[0].from)} ⇄ ${cityName(list[0].to)}`;
    }
    const seq = [list[0].from, ...list.map(l => l.to)];
    return seq.map(cityName).join(' → ');
  }, [search.legs, search.from, search.to, search.tripType]);

  const isLoading = liveStatus === 'searching' && rawResults.length === 0;
  const showResults = liveStatus === 'complete' && rawResults.length > 0;
  const showNoProvider = liveStatus === 'no_provider' || (liveStatus === 'complete' && rawResults.length === 0);
  const priceMax = 3000;

  return (
    <div>
      <section className="bg-teal-800 pt-10 pb-10 md:pt-12 md:pb-12">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-white text-center">
            <div className="text-teal-200 uppercase tracking-widest text-xs font-semibold" data-testid="hero-eyebrow">Flight search</div>
            <h1 className="font-display font-extrabold text-3xl md:text-4xl mt-2" data-testid="hero-heading">Compare Flights</h1>
            <p className="text-white/80 text-sm md:text-base mt-2 max-w-xl mx-auto" data-testid="hero-subheading">
              Search and compare flight options from multiple travel providers.
            </p>
          </div>
        </div>
      </section>

      {/* Cheapora's SearchWidget (below) contains the customer-facing
          Flights/Hotels/Cars tabs, Return/One-Way/Multi-City controls,
          route/date/pax/cabin inputs and the Search flights button. On
          submit it serialises the state into the officially supported
          Aviasales `flightSearch=` URL parameter and Travelpayouts renders
          matching results in #tpwl-tickets. */}

      {CUSTOM_SEARCH_ENABLED && (
      <div className="max-w-7xl mx-auto px-4 md:px-6 mt-6 relative z-10">
        <SearchWidget
          defaultTab="flights"
          compact
          initial={initialSearch}
          key={`${location.key || 'init'}:${initialSearch?.tripType || ''}:${(initialSearch?.legs || []).map(l => `${l.from}-${l.to}-${l.depart}`).join('|') || `${initialSearch?.from || ''}-${initialSearch?.to || ''}-${initialSearch?.depart || ''}`}`}
        />
      </div>
      )}

      {!KAYAK_FLIGHTS_ENABLED && (
        <section
          className="max-w-7xl mx-auto px-4 md:px-6 pt-4 pb-10"
          data-testid="tpwl-mount"
          aria-label="Travelpayouts White Label"
        >
          {/* Official Travelpayouts White Label (wl_id=22421) mount points.
              These must exist in the DOM before the WL loader initialises.
              Do not add Cheapora styling inside; the WL owns rendered UI for
              both containers. */}
          <div id="tpwl-search"></div>
          <div id="tpwl-tickets" className="mt-6"></div>
        </section>
      )}

      {KAYAK_FLIGHTS_ENABLED && (
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 grid grid-cols-1 lg:grid-cols-4 gap-6">
        <aside className="lg:col-span-1">
          <Card className="border-slate-200 sticky top-20">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <Filter className="w-4 h-4 text-teal-700" />
                <div className="font-display font-bold text-slate-900">Filters</div>
              </div>
              <div className="space-y-6">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Max price (S$)</Label>
                  <Slider value={maxPrice} onValueChange={setMaxPrice} min={100} max={priceMax} step={50} className="mt-3" />
                  <div className="text-sm mt-2 text-slate-700">Up to <span className="font-bold text-teal-700">S${maxPrice[0]}</span></div>
                </div>
                <Separator />
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Stops</Label>
                  <div className="mt-3 space-y-2">
                    <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={stops.nonstop} onCheckedChange={(v) => setStops(s => ({ ...s, nonstop: !!v }))} data-testid="filter-nonstop" /> Non-stop</label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={stops.one} onCheckedChange={(v) => setStops(s => ({ ...s, one: !!v }))} data-testid="filter-onestop" /> 1+ stops</label>
                  </div>
                </div>
                <Separator />
                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Airlines</Label>
                  <div className="mt-3 space-y-2 max-h-64 overflow-y-auto pr-1">
                    {airlinesList.map(a => (
                      <label key={a} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox checked={!!airlineFilter[a]} onCheckedChange={(v) => setAirlineFilter(f => ({ ...f, [a]: !!v }))} /> {a}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </aside>

        <div className="lg:col-span-3">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-slate-600 flex items-center gap-2">
              {isLoading && (<><Loader2 className="w-4 h-4 animate-spin text-teal-700" /> Searching partner inventory…</>)}
              {showResults && (<>{filtered.length} flights found</>)}
              {showNoProvider && (<>0 flights · No active flight provider yet</>)}
              {liveStatus === 'error' && (<>Couldn't fetch flights right now.</>)}
            </div>
            <Tabs value={sort} onValueChange={setSort}>
              <TabsList>
                <TabsTrigger value="best">Best</TabsTrigger>
                <TabsTrigger value="cheapest">Cheapest</TabsTrigger>
                <TabsTrigger value="fastest">Fastest</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div className="space-y-3">
            {showResults && <IndicativeResultsNotice />}
            {isLoading && (
              <Card className="border-slate-200">
                <CardContent className="p-10 text-center text-slate-500 flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin text-teal-700" /> Fetching partner inventory…
                </CardContent>
              </Card>
            )}
            {showResults && filtered.map((f) => (
              <Card key={f.id} className="border-slate-200 card-lift" data-testid="flight-card">
                <CardContent className="p-5">
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-12 md:col-span-2 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center font-bold text-slate-700 text-sm">{f.logo}</div>
                      <div>
                        <div className="font-semibold text-sm text-slate-900">{f.airline}</div>
                        <div className="text-xs text-slate-500">{f.cabin}{f.provider ? ` · ${f.provider}` : ''}</div>
                      </div>
                    </div>
                    <div className="col-span-8 md:col-span-6 grid grid-cols-3 items-center">
                      <div>
                        <div className="font-display font-extrabold text-xl text-slate-900">{f.depart}</div>
                        <div className="text-xs text-slate-500">{f.from}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-slate-500 flex items-center justify-center gap-1"><Clock className="w-3 h-3" /> {f.duration}</div>
                        <div className="my-1 h-px bg-slate-300 relative">
                          <Plane className="w-3 h-3 text-teal-700 absolute right-0 -top-1.5" />
                        </div>
                        <div className="text-xs text-slate-500">{f.stops}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-display font-extrabold text-xl text-slate-900">{f.arrive}</div>
                        <div className="text-xs text-slate-500">{f.to}</div>
                      </div>
                    </div>
                    <div className="col-span-4 md:col-span-2 flex flex-wrap gap-1 justify-end">
                      {f.emissions && <Badge variant="outline" className="border-emerald-300 text-emerald-700 gap-1"><Leaf className="w-3 h-3" /> Lower CO₂</Badge>}
                      <Badge variant="outline" className="border-slate-300 text-slate-700 gap-1"><Luggage className="w-3 h-3" /> 7kg</Badge>
                    </div>
                    <div className="col-span-12 md:col-span-2 flex md:flex-col items-end md:items-end justify-between md:justify-center gap-2">
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500">from</div>
                        <div className="font-display font-extrabold text-2xl text-teal-700">
                          {f.price != null ? `S$${f.price}` : '—'}
                        </div>
                      </div>
                      <Button
                        className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold gap-1"
                        onClick={() => {
                          if (f.bookingUrl) { window.open(f.bookingUrl, '_blank', 'noopener,noreferrer'); return; }
                          toast({ title: 'Booking link unavailable', description: 'Please try again in a moment.' });
                        }}
                      >
                        Select <ArrowRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {showNoProvider && (
              <Card className="border-slate-200" data-testid="flights-no-provider">
                <CardContent className="p-10 md:p-14 text-center">
                  <div className="font-display font-extrabold text-xl text-slate-900">
                    No indicative results are currently available for this route
                  </div>
                  <p className="text-slate-600 text-sm mt-2 max-w-lg mx-auto">
                    Please try another origin, destination or date. Live booking availability is
                    coming soon as we finalise our provider integrations.
                  </p>
                </CardContent>
              </Card>
            )}
            {liveStatus === 'error' && (
              <Card className="border-slate-200">
                <CardContent className="p-10 text-center text-slate-500">
                  We couldn't fetch flights right now. Please try again in a moment.
                </CardContent>
              </Card>
            )}
          </div>

        </div>
      </div>
      )}
    </div>
  );
}
